<script>
import BottomSheet from './BottomSheet.vue';
const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

export default {
  name: 'BusinessHoursModal',
  components: { BottomSheet },
  props: {
    show: { type: Boolean, default: false },
    workingHours: { type: Array, required: true },
    timezone: { type: String, required: true },
  },
  emits: ['close'],
  data() {
    return {
      scrollIntervals: {},
    };
  },
  computed: {
    formattedHours() {
      const groups = [];
      this.workingHours.forEach(slot => {
        if (slot.closed_all_day) return;
        const timeStr = slot.open_all_day
          ? 'All day'
          : `${this.formatTime(slot.open_hour, slot.open_minutes)} ~ ${this.formatTime(slot.close_hour, slot.close_minutes)}`;
        const found = groups.find(g => g.time === timeStr);
        if (found) {
          found.days.push(WEEKDAYS[slot.day_of_week]);
        } else {
          groups.push({ time: timeStr, days: [WEEKDAYS[slot.day_of_week]] });
        }
      });

      return groups.map(g => ({
        days: g.days.join(', '),
        time: g.time,
      }));
    },
  },
  mounted() {
    // 组件挂载后，为每个可滚动元素启动自动滚动
    this.$nextTick(() => {
      const scrollElements = document.querySelectorAll('.auto-scroll');
      scrollElements.forEach((element, idx) => {
        this.startAutoScroll(element, idx);
      });
    });
  },
  methods: {
    formatTime(hour, min) {
      const h = hour % 12 || 12;
      const ampm = hour < 12 ? 'AM' : 'PM';
      return `${h}:${min.toString().padStart(2, '0')} ${ampm}`;
    },
    startAutoScroll(element, idx) {
      if (element.scrollWidth <= element.clientWidth) {
        return; // 不需要滚动
      }

      // 清除可能存在的旧定时器
      if (this.scrollIntervals[idx]) {
        clearInterval(this.scrollIntervals[idx]);
      }

      let direction = 1; // 1表示向右滚动，-1表示向左滚动
      let position = 0;

      this.scrollIntervals[idx] = setInterval(() => {
        // 检查是否到达边界
        if (position >= element.scrollWidth - element.clientWidth) {
          // 到达右边界，等待1秒后向左滚动
          setTimeout(() => {
            direction = -1;
          }, 1000);
        } else if (position <= 0 && direction === -1) {
          // 到达左边界，等待1秒后向右滚动
          setTimeout(() => {
            direction = 1;
          }, 1000);
        }

        // 执行滚动
        position += direction * 1; // 每次滚动1像素
        element.scrollLeft = position;
      }, 30); // 30毫秒执行一次，约33fps
    },
    stopAutoScroll(idx) {
      if (this.scrollIntervals[idx]) {
        clearInterval(this.scrollIntervals[idx]);
        delete this.scrollIntervals[idx];
      }
    },
  },
  beforeUnmount() {
    // 清除所有定时器
    Object.values(this.scrollIntervals).forEach(interval => {
      clearInterval(interval);
    });
    this.scrollIntervals = {};
  },
};
</script>

<template>
  <BottomSheet :show="show" @close="$emit('close')">
    <template #header>
      <div class="flex items-center justify-between">
        <div class="font-bold text-base text-n-slate-8">
          {{ $t('BUSINESS_HOURS', 'Business hours') }}
        </div>
        <button
          class="text-gray-400"
          :aria-label="$t('COMMON.CLOSE', 'Close')"
          @click="$emit('close')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              fill="currentColor"
              d="M4.929 4.929a1.5 1.5 0 0 0 0 2.121L9.879 12l-4.95 4.95A1.5 1.5 0 0 0 7.05 19.07l4.95-4.95 4.95 4.95a1.5 1.5 0 0 0 2.121-2.121L14.121 12l4.95-4.95A1.5 1.5 0 1 0 16.95 4.93L12 9.879l-4.95-4.95a1.5 1.5 0 0 0-2.121 0"
            />
          </svg>
        </button>
      </div>
    </template>

    <div class="flex flex-col space-y-4 mt-6">
      <div
        v-for="(slot, idx) in formattedHours"
        :key="idx"
        class="flex items-start"
      >
        <div class="text-green-500 mr-2 mt-1 flex-shrink-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            fill="none"
            viewBox="0 0 24 24"
            data-testid="bezier-react-icon"
          >
            <path
              fill="currentColor"
              fill-rule="evenodd"
              d="M17 12a5 5 0 1 1-10 0 5 5 0 0 1 10 0"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="flex-1 flex items-start justify-between min-w-0">
          <div
            class="overflow-x-auto whitespace-nowrap pr-2 scrollbar-hide mr-4 auto-scroll"
            @mouseenter="stopAutoScroll(idx)"
            @mouseleave="startAutoScroll($event.target, idx)"
            @touchstart="stopAutoScroll(idx)"
            @touchend="startAutoScroll($event.target, idx)"
          >
            <span class="text-base">{{ slot.days }}</span>
          </div>
          <div class="text-base text-right flex-shrink-0">
            {{ slot.time }}
          </div>
        </div>
      </div>

      <div class="text-sm font-medium text-n-slate-8 mt-2 flex items-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          fill="none"
          viewBox="0 0 24 24"
          class="mr-1"
        >
          <path
            fill="currentColor"
            d="M7 12a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1"
          />
        </svg>
        {{ $t('TIMEZONE', 'Timezone') }}: {{ timezone }}
      </div>
    </div>
  </BottomSheet>
</template>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.auto-scroll {
  cursor: pointer;
}
</style>
